import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCarSide, FaEuroSign, FaCalendarAlt, FaPercentage, FaPlus, FaShip } from 'react-icons/fa';
import { FaTruckFast } from 'react-icons/fa6';
import moment from 'moment';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import CommissionReport from '../components/PartnerManagement/CommissionReport';
import CreatePartnerYachtBookingModal from '../components/PartnerManagement/CreatePartnerYachtBookingModal';
import TransferModal from '../components/TransferModal/TransferModal';
import api from '../services/api';

const PartnerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [rentals, setRentals] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [yachtBookings, setYachtBookings] = useState([]);
  const [partner, setPartner] = useState(null);
  const [isCreateRentalModalOpen, setIsCreateRentalModalOpen] = useState(false);
  const [isCreateTransferModalOpen, setIsCreateTransferModalOpen] = useState(false);
  const navigate = useNavigate();  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('partnerToken');
      console.log('Token:', token);
      if (!token) {
        navigate('/partner/login');
        return;
      }

      try {
        const response = await api.auth.getCurrentUser();
        console.log('User data:', response.data);
        if (!response.data.user.isProfessionalPartner) {
          navigate('/partner/login');
          return;
        }
        setPartner(response.data.user);
        fetchPartnerYachtBookings(response.data.user._id);
      } catch (error) {
        console.error('Error checking authentication:', error);
        navigate('/partner/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const fetchPartnerYachtBookings = async (partnerId) => {
    try {
      const response = await api.yachtBookings.getByPartner(partnerId);
      console.log('Yacht bookings:', response.data);
      setYachtBookings(response.data);
    } catch (error) {
      console.error('Error fetching yacht bookings:', error);
    }
  };
  
  const calculateStats = () => {
    const currentMonth = moment().format('YYYY-MM');
    const monthlyRentals = rentals.filter(rental => 
      moment(rental.createdAt).format('YYYY-MM') === currentMonth
    );
    
    const monthlyTransfers = transfers.filter(transfer => 
      moment(transfer.createdAt).format('YYYY-MM') === currentMonth
    );
    
    const monthlyYachtBookings = yachtBookings.filter(booking => 
      moment(booking.createdAt).format('YYYY-MM') === currentMonth
    );    const totalCommissionRentals = rentals.reduce((sum, rental) => sum + (rental.commissionAmount || 0), 0);
    const totalCommissionTransfers = transfers.reduce((sum, transfer) => sum + (transfer.partnerCommission || 0), 0);
    const totalCommissionYachts = yachtBookings.reduce((sum, booking) => sum + (booking.commissionAmount || 0), 0);
    
    const monthlyCommissionRentals = monthlyRentals.reduce((sum, rental) => sum + (rental.commissionAmount || 0), 0);
    const monthlyCommissionTransfers = monthlyTransfers.reduce((sum, transfer) => sum + (transfer.partnerCommission || 0), 0);
    const monthlyCommissionYachts = monthlyYachtBookings.reduce((sum, booking) => sum + (booking.commissionAmount || 0), 0);

    return {
      totalRentals: rentals.length,
      totalTransfers: transfers.length,
      totalYachtBookings: yachtBookings.length,
      monthlyRentals: monthlyRentals.length,
      monthlyTransfers: monthlyTransfers.length,
      monthlyYachtBookings: monthlyYachtBookings.length,
      totalCommission: totalCommissionRentals + totalCommissionTransfers + totalCommissionYachts,
      monthlyCommission: monthlyCommissionRentals + monthlyCommissionTransfers + monthlyCommissionYachts,
      averageCommission: (rentals.length + transfers.length + yachtBookings.length) ? 
        (totalCommissionRentals + totalCommissionTransfers + totalCommissionYachts) / 
        (rentals.length + transfers.length + yachtBookings.length) : 0
    };
  };  
  
  const handleYachtBookingCreated = (booking) => {
    // Add the new yacht booking to the list and refresh data
    fetchPartnerYachtBookings(partner._id);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const stats = calculateStats();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <div className="flex-grow container mx-auto px-4 py-8">        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Partner Dashboard</h1>
            <p className="text-gray-600">Welcome back, {partner?.firstName} {partner?.lastName}</p>
          </div>
          <div className="flex space-x-4">
            <motion.button
              onClick={() => setIsCreateRentalModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 flex items-center space-x-2"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Yacht Booking</span>
            </motion.button>
            
            <motion.button
              onClick={() => setIsCreateTransferModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 flex items-center space-x-2"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Transfer</span>
            </motion.button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Rentals</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRentals}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FaCarSide className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Transfers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTransfers}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FaCalendarAlt className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Yacht Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalYachtBookings}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FaShip className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Commission</p>
                <p className="text-2xl font-bold text-gray-900">€{stats.totalCommission.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <FaEuroSign className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Commission Rate</p>
                <p className="text-2xl font-bold text-gray-900">{partner?.commissionRate}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FaPercentage className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>
        </div>        {/* Commission Report */}
        {partner && (
          <CommissionReport 
            rentals={rentals} 
            transfers={transfers}
            yachtBookings={yachtBookings}
            partner={partner}
            onYachtBookingsUpdate={() => fetchPartnerYachtBookings(partner._id)}
          />
        )}
          <CreatePartnerYachtBookingModal
          isOpen={isCreateRentalModalOpen}
          onClose={() => setIsCreateRentalModalOpen(false)}
          partner={partner}
          onSuccess={handleYachtBookingCreated}
        />
        
      </div>
      <Footer />
    </div>
  );
};

export default PartnerDashboard;